import React, { useEffect, useMemo, useState } from 'react';

export default function MerchantCenterInventoryPerformance() {
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [dateRange, setDateRange] = useState('last12');
  const [sales, setSales] = useState([]);       // monthly category sales
  const [inventory, setInventory] = useState([]); // monthly category inventory
  const [rows, setRows] = useState([]);         // merged rows

  const brandId = useMemo(() => {
    try {
      const current = localStorage.getItem('currentBrandId') || sessionStorage.getItem('currentBrandId');
      if (current) return current;
    } catch (_) {}
    const tryKeys = ['brandId', 'selectedBrandId', 'activeBrandId', 'brand_id'];
    for (const k of tryKeys) {
      try { const v = localStorage.getItem(k) || sessionStorage.getItem(k); if (v) return v; } catch (_) {}
    }
    const tryJson = ['selectedBrand', 'currentBrand', 'activeBrand'];
    for (const k of tryJson) {
      try {
        const raw = localStorage.getItem(k) || sessionStorage.getItem(k);
        if (raw) { const obj = JSON.parse(raw); if (obj?.id) return obj.id; }
      } catch(_) {}
    }
    try {
      const m = window.location.pathname.match(/[0-9a-fA-F-]{36}/);
      if (m) return m[0];
    } catch(_) {}
    return '';
  }, []);

  const normalize = (s) => String(s || '').trim().toLowerCase();
  const ym = (y, m) => `${String(y).padStart(4,'0')}-${String(m).padStart(2,'0')}`;

  useEffect(() => {
    const load = async () => {
      if (!brandId) return;
      try {
        setLoading(true);
        setErrorMsg('');

        const headers = { 'Content-Type': 'application/json', 'X-Brand-ID': brandId };
        const opts = { headers, credentials: 'include' };

        const [sRes, iRes] = await Promise.all([
          fetch(`https://api.glowytics.com/api/google-ads/monthly-category-summary?dateRange=${encodeURIComponent(dateRange)}`, opts),
          fetch(`https://api.glowytics.com/api/google-ads/merchant-center/monthly-category-inventory?dateRange=${encodeURIComponent(dateRange)}`, opts)
        ]);

        const sText = await sRes.text();
        const iText = await iRes.text();
        let sJson, iJson;
        try { sJson = JSON.parse(sText); } catch { sJson = {}; }
        try { iJson = JSON.parse(iText); } catch { iJson = {}; }
        if (!sRes.ok) throw new Error(sJson?.error || sRes.statusText || 'sales error');
        if (!iRes.ok) throw new Error(iJson?.error || iRes.statusText || 'inventory error');

        const sData = Array.isArray(sJson?.data) ? sJson.data : [];
        const iData = Array.isArray(iJson?.rows) ? iJson.rows : (Array.isArray(iJson?.data) ? iJson.data : []);

        setSales(sData);
        setInventory(iData);

        // Merge by (year,month,normalized category)
        const map = new Map();
        const prevMonthKey = (y, m) => {
          let yy = Number(y || 0), mm = Number(m || 0);
          mm -= 1;
          if (mm < 1) { mm = 12; yy -= 1; }
          return ym(yy, mm);
        };

        for (const r of sData) {
          const key = `${ym(r.year, r.month)}|${normalize(r.itemCategory)}`;
          map.set(key, {
            year: Number(r.year || 0),
            month: Number(r.month || 0),
            monthKey: ym(r.year, r.month),
            category: r.itemCategory || '(unknown)',
            totalRevenue: Number(r.totalRevenue || 0),
            totalItemsPurchased: Number(r.totalItemsPurchased || 0),
            totalItemsViewed: Number(r.totalItemsViewed || 0),
            inStockProducts: null,
            productCount: null,
            outOfStockProducts: null,
            avgEffectivePrice: null,
            revenueDeltaMoM: null,
            inStockDeltaMoM: null
          });
        }

        for (const r of iData) {
          const cat = r.category || r.item_category || '(unknown)';
          const key = `${ym(r.year, r.month)}|${normalize(cat)}`;
          const prev = map.get(key) || {
            year: Number(r.year || 0),
            month: Number(r.month || 0),
            monthKey: ym(r.year, r.month),
            category: cat,
            totalRevenue: 0,
            totalItemsPurchased: 0,
            totalItemsViewed: 0,
            inStockProducts: null,
            productCount: null,
            outOfStockProducts: null,
            avgEffectivePrice: null,
            revenueDeltaMoM: null,
            inStockDeltaMoM: null
          };
          prev.inStockProducts = Number(r.in_stock_products || 0);
          prev.productCount = Number(r.product_count || 0);
          prev.outOfStockProducts = Number(r.out_of_stock_products || 0);
          prev.avgEffectivePrice = r.avg_effective_price != null ? Number(r.avg_effective_price) : null;
          // prefer more descriptive category if one is '(unknown)'
          if (normalize(prev.category) === '(unknown)' && cat) prev.category = cat;
          map.set(key, prev);
        }

        const merged = Array.from(map.values()).sort((a, b) => {
          if (a.year !== b.year) return b.year - a.year;
          if (a.month !== b.month) return b.month - a.month;
          return (b.totalRevenue || 0) - (a.totalRevenue || 0);
        });

        // Compute MoM deltas per category
        const index = new Map(merged.map(r => [`${r.monthKey}|${normalize(r.category)}`, r]));
        for (const r of merged) {
          const pmKey = `${prevMonthKey(r.year, r.month)}|${normalize(r.category)}`;
          const prev = index.get(pmKey);
          if (prev) {
            r.revenueDeltaMoM = (Number(r.totalRevenue || 0) - Number(prev.totalRevenue || 0));
            const curIn = r.inStockProducts != null ? Number(r.inStockProducts) : null;
            const prevIn = prev.inStockProducts != null ? Number(prev.inStockProducts) : null;
            r.inStockDeltaMoM = (curIn != null && prevIn != null) ? (curIn - prevIn) : null;
          }
        }
        setRows(merged);
      } catch (e) {
        setErrorMsg(e?.message || 'Load error');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [brandId, dateRange]);

  const totals = useMemo(() => {
    return rows.reduce((acc, r) => {
      acc.revenue += Number(r.totalRevenue || 0);
      acc.inStock += Number(r.inStockProducts || 0);
      acc.count += 1;
      return acc;
    }, { revenue: 0, inStock: 0, count: 0 });
  }, [rows]);

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <img src="/merchantcenterlogo.svg" alt="Merchant Center" className="h-10" />
          <div className="mt-2 text-lg font-semibold text-gray-900">Inventory Performance</div>
          <div className="mt-3 flex items-center gap-2">
            <label className="text-sm text-gray-600">Date range</label>
            <select
              value={dateRange}
              onChange={(e)=>setDateRange(e.target.value)}
              className="text-sm border rounded px-2 py-1"
            >
              <option value="last3">Last 3 months</option>
              <option value="last6">Last 6 months</option>
              <option value="last12">Last 12 months</option>
              <option value="all">All</option>
            </select>
          </div>
        </div>

        {errorMsg ? <div className="text-sm text-red-600 mb-4">{errorMsg}</div> : null}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-2xl border shadow-sm p-5">
            <div className="text-sm text-gray-500">Total Revenue</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">
              {loading ? '...' : totals.revenue.toLocaleString('tr-TR')}
            </div>
          </div>
          <div className="bg-white rounded-2xl border shadow-sm p-5">
            <div className="text-sm text-gray-500">Total In-Stock (sum across months)</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">
              {loading ? '...' : totals.inStock.toLocaleString('tr-TR')}
            </div>
          </div>
          <div className="bg-white rounded-2xl border shadow-sm p-5">
            <div className="text-sm text-gray-500">Rows</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">
              {loading ? '...' : totals.count.toLocaleString('tr-TR')}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium text-gray-800">Monthly Category: Revenue vs In-Stock</div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left">month</th>
                  <th className="px-3 py-2 text-left">category</th>
                  <th className="px-3 py-2 text-right">revenue</th>
                  <th className="px-3 py-2 text-right">rev Δ MoM</th>
                  <th className="px-3 py-2 text-right">items purchased</th>
                  <th className="px-3 py-2 text-right">in stock products</th>
                  <th className="px-3 py-2 text-right">in-stock Δ MoM</th>
                  <th className="px-3 py-2 text-right">total products</th>
                  <th className="px-3 py-2 text-right">avg price</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td className="px-3 py-3 text-gray-500" colSpan={9}>Loading…</td></tr>
                ) : rows.length ? rows.map((r, i) => (
                  <tr key={`${r.monthKey}-${r.category}-${i}`} className="border-t">
                    <td className="px-3 py-2">{r.monthKey}</td>
                    <td className="px-3 py-2 max-w-[360px] truncate" title={r.category}>{r.category}</td>
                    <td className="px-3 py-2 text-right">{Number(r.totalRevenue || 0).toLocaleString('tr-TR')}</td>
                    <td className={`px-3 py-2 text-right ${Number(r.revenueDeltaMoM || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {r.revenueDeltaMoM == null ? '-' : Number(r.revenueDeltaMoM).toLocaleString('tr-TR')}
                    </td>
                    <td className="px-3 py-2 text-right">{Number(r.totalItemsPurchased || 0).toLocaleString('tr-TR')}</td>
                    <td className="px-3 py-2 text-right">{r.inStockProducts != null ? Number(r.inStockProducts).toLocaleString('tr-TR') : '-'}</td>
                    <td className={`px-3 py-2 text-right ${Number(r.inStockDeltaMoM || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {r.inStockDeltaMoM == null ? '-' : Number(r.inStockDeltaMoM).toLocaleString('tr-TR')}
                    </td>
                    <td className="px-3 py-2 text-right">{r.productCount != null ? Number(r.productCount).toLocaleString('tr-TR') : '-'}</td>
                    <td className="px-3 py-2 text-right">{r.avgEffectivePrice != null ? Number(r.avgEffectivePrice).toLocaleString('tr-TR') : '-'}</td>
                  </tr>
                )) : (
                  <tr><td className="px-3 py-3 text-gray-500" colSpan={9}>No data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}


